import React, { useState, useEffect, useMemo } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { alpha, Box, Button, Card, Theme, CardContent, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, Menu, Select, MenuItem, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Datagrid, ListContextProvider, TextField, DateField, BooleanField, NumberField, EditButton, Pagination, Title, useDataProvider, useList, useRecordContext, useRefresh, useNotify } from 'react-admin';
import ConfirmedActionButton from '../../Components/ConfirmedActionButton';
import StartStopIntegrationButton from '../../Components/StartStopIntegrationButton';
import { ALLOWED_CONFIG_EDITORS } from './ConfigEditor/ConfigEditor';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';
import { normalizeIntegrationRecord } from '../../types/integrations';

// Theme colours, so each status stays readable in the light and the dark theme alike.
const statusColors: Record<string, 'success' | 'error' | 'warning'> = {
  STARTED: 'success',
  DISABLED: 'error',
  PAUSED: 'warning'
};

const StatusField: React.FC<{ source?: string; label?: string; sortable?: boolean }> = () => {
  const record = useRecordContext();
  if (!record) return null;
  const status = record.status as string;
  const tone = statusColors[status];
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        bgcolor: (theme: Theme) => (tone ? alpha(theme.palette[tone].main, 0.15) : theme.palette.action.selected),
        // The light theme's main orange is too pale on its own tint; its dark shade reads, and the dark theme's main does.
        color: (theme: Theme) => (tone ? theme.palette[tone][theme.palette.mode === 'dark' ? 'main' : 'dark'] : theme.palette.text.secondary),
        fontWeight: 600
      }}
    />
  );
};

const IntegrationActions: React.FC<{ label?: string }> = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const userEmail = JSON.parse(localStorage.getItem('GoogleAccountInfo') || '{}').email || '';
  const canEdit = ALLOWED_CONFIG_EDITORS.includes(userEmail);

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <StartStopIntegrationButton />
      <Tooltip title="Forces the integration to start without checking its last sync date">
        <span>
          <ConfirmedActionButton
            label="Run"
            icon={<PlayArrowIcon />}
            confirmTitle="Confirm Run"
            confirmContent={`Are you sure you want to run this integration (${record.uuid})?`}
            onConfirm={async () => {
              await (dataProvider as any).runIntegration(record.uuid);
              refresh();
              notify('Integration run triggered successfully', { type: 'success' });
            }}
            color="success"
          />
        </span>
      </Tooltip>
      <Tooltip title="Clears the integration's last sync date">
        <span>
          <ConfirmedActionButton
            label="Reset"
            icon={<RestartAltIcon />}
            confirmTitle="Confirm Reset"
            confirmContent={`Are you sure you want to reset this integration (${record.uuid})?`}
            onConfirm={async () => {
              await (dataProvider as any).resetIntegration(record.uuid);
              refresh();
              notify('Integration reset successfully', { type: 'success' });
            }}
            color="warning"
          />
        </span>
      </Tooltip>
      <Tooltip title="Resets all employee and request cache data for this integration, forcing a full update on the next tick">
        <span>
          <ConfirmedActionButton
            label="Clear"
            icon={<ClearIcon />}
            confirmTitle="Confirm Clear Cache"
            confirmContent={`Are you sure you want to clear the cache for this integration (${record.uuid})?`}
            onConfirm={async () => {
              await (dataProvider as any).clearIntegrationCache(record.uuid);
              refresh();
              notify('Cache cleared successfully', { type: 'success' });
            }}
            color="error"
          />
        </span>
      </Tooltip>
      {canEdit && <EditButton />}
    </Box>
  );
};

const useIntegrationData = () => {
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('./integrationsMockData')
        .then(({ integrationsMockData }) => {
          setAllData(integrationsMockData.map((item: any) => normalizeIntegrationRecord({ ...item, id: item.uuid })));
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
      return;
    }

    const query = `sort=${encodeURIComponent(JSON.stringify(['companyName', 'ASC']))}&range=${encodeURIComponent(JSON.stringify([0, 9999]))}&filter=${encodeURIComponent(JSON.stringify({}))}`;
    getAuthProviderFetchJson(`${superAdminApiServer}/integrations?${query}`)
      .then(({ json }: any) => {
        const items = Array.isArray(json) ? json : json.data || json.items || [];
        setAllData(items.map((item: any) => normalizeIntegrationRecord({ ...item, id: item.uuid })));
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  return { allData, isLoading };
};

const useUniqueValues = (data: any[], field: string): string[] => {
  return useMemo(() => {
    if (!data.length) return [];
    return Array.from(new Set(data.map((d) => d[field])))
      .filter(Boolean)
      .sort() as string[];
  }, [data, field]);
};

interface IntegrationFiltersProps {
  data: any[];
  filterValues: Record<string, any>;
  setFilters: (filters: any, displayedFilters?: any) => void;
}

const IntegrationFilters: React.FC<IntegrationFiltersProps> = ({ data, filterValues, setFilters }) => {
  const companies = useUniqueValues(data, 'companyName');
  const vendors = useUniqueValues(data, 'vendor');
  const sources = useUniqueValues(data, 'source');
  const statuses = useUniqueValues(data, 'status');

  const handleChange = (field: string, value: string) => {
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters[field];
    } else {
      newFilters[field] = value;
    }
    setFilters(newFilters);
  };

  const handleBooleanFilterChange = (field: string, value: string) => {
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters[field];
    } else {
      newFilters[field] = value === 'true';
    }
    setFilters(newFilters);
  };

  const lastSyncValue = filterValues.lastSyncSuccess === true ? 'true' : filterValues.lastSyncSuccess === false ? 'false' : '';
  const booleanValue = filterValues.isOtCredentialsValid === true ? 'true' : filterValues.isOtCredentialsValid === false ? 'false' : '';

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Company</InputLabel>
        <Select label="Company" value={filterValues.companyName || ''} onChange={(e) => handleChange('companyName', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {companies.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Vendor</InputLabel>
        <Select label="Vendor" value={filterValues.vendor || ''} onChange={(e) => handleChange('vendor', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {vendors.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Source</InputLabel>
        <Select label="Source" value={filterValues.source || ''} onChange={(e) => handleChange('source', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {sources.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={filterValues.status || ''} onChange={(e) => handleChange('status', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {statuses.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Last Sync</InputLabel>
        <Select label="Last Sync" value={lastSyncValue} onChange={(e) => handleBooleanFilterChange('lastSyncSuccess', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="true">Success</MenuItem>
          <MenuItem value="false">Failed</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>OT Credentials Valid</InputLabel>
        <Select label="OT Credentials Valid" value={booleanValue} onChange={(e) => handleBooleanFilterChange('isOtCredentialsValid', e.target.value as string)}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

interface ColumnDef {
  key: string;
  label: string;
  width: number;
  defaultVisible: boolean;
  element: React.ReactElement;
}

const LastSyncErrorField: React.FC<{ source?: string; label?: string; sortable?: boolean }> = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  if (!record?.errorMessage)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  return (
    <>
      <Typography
        variant="body2"
        sx={{ cursor: 'pointer', color: 'error.main', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {record.errorMessage}
      </Typography>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Last Sync Error — {record.companyName}
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem', p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
            {record.errorMessage}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'uuid', label: 'Integration UUID', width: 280, defaultVisible: false, element: <TextField source="uuid" label="Integration UUID" sortable /> },
  { key: 'companyUuid', label: 'Company UUID', width: 280, defaultVisible: false, element: <TextField source="companyUuid" label="Company UUID" sortable /> },
  { key: 'companyName', label: 'Company', width: 150, defaultVisible: true, element: <TextField source="companyName" label="Company" sortable /> },
  { key: 'vendor', label: 'Vendor', width: 150, defaultVisible: true, element: <TextField source="vendor" label="Vendor" sortable /> },
  { key: 'source', label: 'Source', width: 120, defaultVisible: true, element: <TextField source="source" label="Source" sortable /> },
  { key: 'status', label: 'Status', width: 100, defaultVisible: true, element: <StatusField source="status" label="Status" sortable /> },
  { key: 'baseUrl', label: 'Base URL', width: 250, defaultVisible: false, element: <TextField source="baseUrl" label="Base URL" sortable={false} /> },
  { key: 'syncInterval', label: 'Sync Interval', width: 120, defaultVisible: false, element: <NumberField source="syncInterval" label="Sync Interval" sortable /> },
  { key: 'lastSyncSuccess', label: 'Success', width: 80, defaultVisible: false, element: <BooleanField source="lastSyncSuccess" label="Success" sortable={false} /> },
  { key: 'isOtCredentialsValid', label: 'Valid OT Credentials', width: 80, defaultVisible: false, element: <BooleanField source="isOtCredentialsValid" label="Valid OT Credentials" sortable={false} /> },
  { key: 'minutesToProcess', label: 'Minutes to Process', width: 140, defaultVisible: false, element: <NumberField source="minutesToProcess" label="Minutes to Process" sortable /> },
  { key: 'lastSyncStartedAt', label: 'Last Start', width: 160, defaultVisible: false, element: <DateField source="lastSyncStartedAt" label="Last Start" showTime sortable={false} /> },
  { key: 'lastSyncSuccessDate', label: 'Last Success', width: 160, defaultVisible: false, element: <DateField source="lastSyncSuccessDate" label="Last Success" showTime sortable /> },
  { key: 'lastRunDate', label: 'Last Run', width: 160, defaultVisible: false, element: <DateField source="lastRunDate" label="Last Run" showTime sortable /> },
  { key: 'errorMessage', label: 'Last Error Message', width: 300, defaultVisible: true, element: <LastSyncErrorField source="errorMessage" label="Last Error Message" sortable={false} /> },
  { key: 'createdAt', label: 'Created At', width: 160, defaultVisible: false, element: <DateField source="createdAt" label="Created At" showTime sortable={false} /> },
  { key: 'updatedAt', label: 'Updated At', width: 160, defaultVisible: false, element: <DateField source="updatedAt" label="Updated At" showTime sortable={false} /> },
  { key: 'actions', label: 'Actions', width: 360, defaultVisible: true, element: <IntegrationActions label="Actions" /> }
];

const ColumnSelector: React.FC<{ visibleKeys: Set<string>; onToggle: (key: string) => void }> = ({ visibleKeys, onToggle }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <Button size="small" startIcon={<ViewColumnIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
        Columns
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {COLUMN_DEFS.map((col) => (
          <MenuItem key={col.key} dense onClick={() => onToggle(col.key)}>
            <FormControlLabel control={<Checkbox checked={visibleKeys.has(col.key)} size="small" />} label={col.label} onClick={(e) => e.stopPropagation()} onChange={() => onToggle(col.key)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const IntegrationsList: React.FC = () => {
  const { allData, isLoading } = useIntegrationData();
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(() => new Set(COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.key)));

  const handleToggle = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const visibleColumns = COLUMN_DEFS.filter((c) => visibleKeys.has(c.key));

  const columnWidthSx = useMemo(() => {
    const sx: Record<string, any> = {};
    visibleColumns.forEach((col, i) => {
      const n = i + 1;
      const w = `${col.width}px`;
      sx[`& th:nth-of-type(${n}), & td:nth-of-type(${n})`] = { width: w, maxWidth: w, minWidth: w };
    });
    return sx;
  }, [visibleColumns]);

  const listContext = useList({
    data: allData,
    isLoading,
    perPage: 25,
    sort: { field: 'companyName', order: 'ASC' }
  });

  return (
    <div>
      <Title title="Integrations" />
      <ListContextProvider value={listContext}>
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <IntegrationFilters data={allData} filterValues={listContext.filterValues} setFilters={listContext.setFilters} />
              <ColumnSelector visibleKeys={visibleKeys} onToggle={handleToggle} />
            </Box>
          </CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <Datagrid
              sx={{
                tableLayout: 'fixed',
                width: 'max-content',
                minWidth: '100%',
                image: { maxHeight: '1rem' },
                '& .RaDatagrid-headerCell': { fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                '& td, & th': { overflow: 'hidden !important', textOverflow: 'ellipsis !important', whiteSpace: 'nowrap !important', boxSizing: 'border-box' },
                '& td:not(:last-of-type) > *, & th:not(:last-of-type) > *': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' },
                '& td:last-of-type': { overflow: 'visible !important' },
                '& .MuiTableSortLabel-root': { textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis' },
                '& .MuiTableSortLabel-icon': { opacity: '1 !important', flexShrink: 0 },
                '& .RaBooleanField-root .MuiSvgIcon-root': { fontSize: '20px', verticalAlign: 'middle' },
                ...columnWidthSx
              }}
              bulkActionButtons={false}
              rowClick="show"
            >
              {visibleColumns.map((col) => React.cloneElement(col.element, { key: col.key }))}
            </Datagrid>
          </Box>
        </Card>
        <Pagination rowsPerPageOptions={[10, 25, 50, 100, 1000]} />
      </ListContextProvider>
    </div>
  );
};

export default IntegrationsList;
